//import React, { useState, useEffect, useCallback } from "react";
import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SongListItem from "../components/SongListItem";
import { fetchSongs, searchSongs, updateSelectedLangs } from "../store/actions/songs";
import '../stylesheets/styles.css'
import Popup from "reactjs-popup";
import { ReactComponent as SettingsIcon } from '../assets/settings.svg';
import LanguageOptions from "../components/LanguageOptions";
import { Helmet } from "react-helmet-async";

/**
 * HomePage Component
 * Main page component that displays the list of songs and handles search functionality
 * Features:
 * - Displays list of songs
 * - Search functionality
 * - Language filtering
 * - Dynamic page title and meta tags from runtime config
 */
const HomePage = () => {
    // Redux state and dispatch
    const {loadingSongs, songs, availableLangs, selectedLangs, hymnforgeInfo} = useSelector(state => state.songs)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Read branding from runtime config (set in index.js from public/config.json)
    const config = window.__HYMNFORGE_CONFIG__ || {};
    const title        = config.site_name        || 'HymnForge Songs';
    const description  = config.site_description || 'Discover, search, and enjoy a collection of songs and hymns.';
    const siteUrl      = config.site_url          || window.location.origin;
    const logoFile     = config.logo              || null;
    // logo_position: "center" (default) or "top_left"
    const logoPosition = config.logo_position     || 'center';

    /**
     * Fetch songs data from the API
     * Memoized using useCallback to prevent unnecessary re-renders
     */
    const fetchData = useCallback((searchQuery) => {
        dispatch(fetchSongs(searchQuery));
    }, [dispatch]);

    // Initial data fetch on component mount
    useEffect(() => {
        fetchData('');
    }, [fetchData]);

    const searchSongHandler = searchKeyword => {
        dispatch(searchSongs(searchKeyword))
    }

    const handleOnClick = songItem => {
        navigate(`/song/${songItem.id}`)
    }

    const handleUpdateSelectedLangs = langs => {
        console.log('make updates in selected langs', langs);
        dispatch(updateSelectedLangs(langs));
    }

    // Language settings popup component
    const LanguageSettingIcon = (
        <Popup
            trigger={<div><SettingsIcon className="setting-icon" /></div>}
            modal>
            {close => (
                <div className="overlay">
                    <button className="close" onClick={close}>
                        &times;
                    </button>
                    <LanguageOptions
                        availableLangs={availableLangs}
                        selectedLangs={selectedLangs}
                        updateSelectedLangs={handleUpdateSelectedLangs}
                        hymnforgeInfo={hymnforgeInfo}
                        close={close}
                    />
                </div>
            )}
        </Popup>
    );

    // Map songs to list items
    const listItems = songs.map((s, i) => (
        <SongListItem
            key={`${s.id}-${s.sort_order}`}
            item={s}
            index={i}
            onClick={handleOnClick.bind(this, s)}
        />
    ));

    // Logo rendered differently depending on logo_position
    const LogoTopLeft = logoFile ? (
        <img
            src={`/${logoFile}`}
            alt={title}
            className="site-logo site-logo-top-left"
        />
    ) : null;

    const LogoCentered = logoFile ? (
        <img
            src={`/${logoFile}`}
            alt={title}
            className="site-logo site-logo-center"
        />
    ) : null;

    // Render component
    return (
        <>
            {/* SEO meta tags */}
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={siteUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
            </Helmet>

            {/* Top-left logo sits outside the centered column, fixed to the page corner */}
            {logoPosition === 'top_left' && LogoTopLeft}

            <div className="home-div">
                {LanguageSettingIcon}
                <div className="list-body">
                    {/* Centered logo sits inside the column, above the title */}
                    {logoPosition === 'center' && LogoCentered}
                    {/* When a centered logo is present, tighten the gap between
                        it and the title. Otherwise the default CSS margin applies. */}
                    <h1 style={logoFile && logoPosition === 'center' ? { marginTop: '5px' } : undefined}>{title}</h1>
                    {(loadingSongs && songs.length === 0) ? (
                        <></>
                    ) : (
                        <input
                            className="search-input"
                            placeholder="search here..."
                            onChange={(e) => searchSongHandler(e.target.value)}
                        />
                    )}
                    {(loadingSongs && songs.length === 0) ? (
                        <div className="loading-text">Loading Songs Data</div>
                    ) : songs.length === 0 ? (
                        <div className="no-songs-message">
                            <h2>No songs are available</h2>
                        </div>
                    ) : (
                        listItems
                    )}
                </div>
            </div>
        </>
    );
};

export default HomePage
